import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Filter20Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  Dismiss20Regular,
} from '@fluentui/react-icons';

interface FiltersSidebarProps {
  /** Whether the panel is collapsed. Controlled by the page (typically wired
   *  to a header toggle). The panel stays mounted in both states so the
   *  width transition runs in both directions. */
  collapsed: boolean;
  /** Number shown in the header badge — e.g. number of filter dimensions
   *  with a non-empty selection. Hidden when zero. */
  activeFilterCount: number;
  /** Toggles the disabled state on the Clear All button. */
  hasActiveFilters: boolean;
  /** Click handler for the Clear All button. */
  onClearAll: () => void;
  /** Filter sections — typically `<FilterSection>` blocks with chips/inputs. */
  children: React.ReactNode;
  /** Which edge the sidebar is anchored to. Drives the divider side
   *  (border-right for left-anchored, border-left for right-anchored).
   *  Default 'left' for backward compat. */
  side?: 'left' | 'right';
  /** Optional override for the header label when "Filters" doesn't fit
   *  the use case (e.g., a profile-page side panel). */
  title?: string;
  /** When true, hide the Clear All footer (e.g., side panels that
   *  aren't filter-driven). */
  hideClearAll?: boolean;
  /** Mobile-only: invoked when the backdrop is tapped. Pages should
   *  wire this to the same handler their toolbar toggle uses (i.e. set
   *  `sidebarCollapsed = true`). When omitted, the backdrop is still
   *  shown but tapping it does nothing. */
  onRequestClose?: () => void;
}

const useStyles = makeStyles({
  expanded: {
    width: '220px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground3,
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    transitionProperty: 'width, border-right-width, border-left-width, transform',
    transitionDuration: '180ms',
    transitionTimingFunction: 'ease',
    willChange: 'width',
    '@media (max-width: 768px)': {
      position: 'fixed',
      top: 0,
      bottom: 0,
      width: '85vw',
      maxWidth: '320px',
      zIndex: 200,
      boxShadow: tokens.shadow16,
    },
  },
  expandedLeft: {
    ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
    '@media (max-width: 768px)': {
      left: 0,
    },
  },
  expandedRight: {
    ...shorthands.borderLeft('1px', 'solid', tokens.colorNeutralStroke2),
    '@media (max-width: 768px)': {
      right: 0,
    },
  },
  collapsedState: {
    width: '0px',
    borderRightWidth: '0px',
    borderLeftWidth: '0px',
  },
  collapsedLeftMobile: {
    '@media (max-width: 768px)': {
      transform: 'translateX(-100%)',
      width: '85vw',
      maxWidth: '320px',
      borderRightWidth: '0px',
    },
  },
  collapsedRightMobile: {
    '@media (max-width: 768px)': {
      transform: 'translateX(100%)',
      width: '85vw',
      maxWidth: '320px',
      borderLeftWidth: '0px',
    },
  },
  backdrop: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'block',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      zIndex: 199,
    },
  },
  // Fixed-width inner so children don't reflow during the width animation.
  inner: {
    width: '220px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('12px', '16px'),
  },
  // Explicit dismiss affordance — only needed on mobile where the panel is a
  // slide-in overlay (on desktop it's collapsed via the page's header toggle).
  closeButton: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'inline-flex',
    },
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    fontSize: '14px',
    fontWeight: 600,
  },
  activeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '18px',
    height: '18px',
    fontSize: '11px',
    fontWeight: 600,
    ...shorthands.padding('0', '6px'),
    ...shorthands.borderRadius('9px'),
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.gap('20px'),
    flexGrow: 1,
  },
  footer: {
    // Padding matches the DataTable's bottom band so the Clear All footer
    // and the table pagination footer share the same Y baseline.
    ...shorthands.padding('24px', '16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
});

/**
 * Generic left-rail filter panel shell. Owns the collapse animation, header
 * (with active-filter badge), scrollable body, and Clear All footer. Page-
 * specific filter UIs are passed in via children — typically wrapped in
 * `<FilterSection>` blocks.
 */
const FiltersSidebar: React.FC<FiltersSidebarProps> = ({
  collapsed,
  activeFilterCount,
  hasActiveFilters,
  onClearAll,
  children,
  side = 'left',
  title,
  hideClearAll = false,
  onRequestClose,
}) => {
  const styles = useStyles();
  const sideClass = side === 'right' ? styles.expandedRight : styles.expandedLeft;
  const mobileCollapseClass = collapsed
    ? (side === 'right' ? styles.collapsedRightMobile : styles.collapsedLeftMobile)
    : '';
  return (
    <>
      {!collapsed && (
        <div
          className={styles.backdrop}
          onClick={onRequestClose}
          aria-hidden="true"
        />
      )}
    <div
      className={`${styles.expanded} ${sideClass} ${collapsed ? styles.collapsedState : ''} ${mobileCollapseClass}`}
      aria-hidden={collapsed}
    >
      <div className={styles.inner}>
        <div className={styles.header}>
          <span className={styles.headerTitle}>
            {!hideClearAll && <Filter20Regular />}
            {title || 'Filters'}
            {hasActiveFilters && <span className={styles.activeBadge}>{activeFilterCount}</span>}
          </span>
          <Button
            appearance="subtle"
            size="small"
            icon={<Dismiss20Regular />}
            className={styles.closeButton}
            onClick={onRequestClose}
            aria-label="Close"
          />
        </div>
        <div className={styles.body}>{children}</div>
        {!hideClearAll && (
          <div className={styles.footer}>
            <Button
              appearance="secondary"
              onClick={onClearAll}
              disabled={!hasActiveFilters}
              style={{ width: '100%' }}
            >
              Clear all filters
            </Button>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default FiltersSidebar;

// ── Reusable subcomponents ────────────────────────────────────────────────

const useSectionStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
  // Header is a button so keyboard users can toggle it; styled to look
  // like the original static label, with the chevron sitting flush right.
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    ...shorthands.padding('0'),
    ...shorthands.margin('0'),
    width: '100%',
    fontFamily: 'inherit',
    textAlign: 'left',
    '&:hover': {
      // Subtle bump on hover so the header reads as interactive without
      // borrowing button chrome.
      color: tokens.colorNeutralForeground1,
    },
  },
  label: {
    fontSize: '12px',
    fontWeight: 700,
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground1,
    letterSpacing: '0.04em',
  },
  chevron: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    display: 'inline-flex',
  },
  emptyHint: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

interface FilterSectionProps {
  label: string;
  /** Render an italic empty-state hint under the label instead of children. */
  emptyHint?: string;
  /** Initial open state. Defaults to true. State is local to the section
   *  (not persisted) — sessions reset to defaultOpen. */
  defaultOpen?: boolean;
  children?: React.ReactNode;
}

export const FilterSection: React.FC<FilterSectionProps> = ({
  label,
  emptyHint,
  defaultOpen = true,
  children,
}) => {
  const styles = useSectionStyles();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={styles.section}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={styles.label}>{label}</span>
        <span className={styles.chevron}>
          {open ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
        </span>
      </button>
      {open && (emptyHint ? <span className={styles.emptyHint}>{emptyHint}</span> : children)}
    </div>
  );
};

// ── Checkbox row pattern ──────────────────────────────────────────────────
// Used for filter dimensions where counts matter (status, payment status):
// [ ] Label …………………………………………… 42

const useCheckboxStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('6px'),
    cursor: 'pointer',
    // 3px top/bot gives ~6px of inter-row spacing once rows are stacked —
    // tighter than the default Checkbox row but still a comfortable click
    // target.
    ...shorthands.padding('3px', '4px'),
    ...shorthands.borderRadius('4px'),
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  // Strip Fluent's intrinsic Checkbox padding so the box hugs the row's top
  // and bottom — rows then end up driven by the text height alone.
  checkbox: {
    ...shorthands.padding('0'),
    ...shorthands.margin('0'),
    minHeight: 'auto',
    '& > .fui-Checkbox__indicator': {
      ...shorthands.margin('0'),
    },
  },
  label: {
    flexGrow: 1,
    minWidth: 0,
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  count: {
    fontSize: '12px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    fontVariantNumeric: 'tabular-nums',
  },
});

interface FilterCheckboxRowProps {
  checked: boolean;
  onChange: () => void;
  label: string;
  /** Right-aligned count. Hidden when undefined (vs. shown as 0). */
  count?: number;
}

export const FilterCheckboxRow: React.FC<FilterCheckboxRowProps> = ({
  checked,
  onChange,
  label,
  count,
}) => {
  const styles = useCheckboxStyles();
  return (
    <div className={styles.row} onClick={onChange} role="button" aria-pressed={checked}>
      <Checkbox
        className={styles.checkbox}
        checked={checked}
        // Stop the click from bubbling to the row's onClick — toggling the
        // checkbox directly would otherwise fire the handler twice.
        onClick={(e) => e.stopPropagation()}
        onChange={onChange}
      />
      <span className={styles.label}>{label}</span>
      {count !== undefined && <span className={styles.count}>{count}</span>}
    </div>
  );
};

export const FilterCheckboxList: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const styles = useCheckboxStyles();
  return <div className={styles.list}>{children}</div>;
};
